import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import {
  Sun, Zap, ArrowRight,
  CheckCircle, RefreshCw, PhoneCall, ChevronRight
} from "lucide-react";
import toast from "react-hot-toast";

const APPLIANCES_PRESET = [
  { id: "lights",   name: "LED Lighting Bulbs",        watts: 15,   defaultQty: 6, defaultHours: 8,  icon: "💡" },
  { id: "fans",     name: "Standing / Ceiling Fans",   watts: 70,   defaultQty: 3, defaultHours: 10, icon: "🌀" },
  { id: "tv",       name: "Smart TV & Sound System",   watts: 120,  defaultQty: 1, defaultHours: 6,  icon: "📺" },
  { id: "fridge",   name: "Energy-Saver Refrigerator", watts: 250,  defaultQty: 1, defaultHours: 12, icon: "🧊" },
  { id: "freezer",  name: "Deep Chest Freezer",        watts: 350,  defaultQty: 0, defaultHours: 10, icon: "❄️" },
  { id: "laptop",   name: "Laptops & Wi-Fi Routers",   watts: 80,   defaultQty: 2, defaultHours: 8,  icon: "💻" },
  { id: "ac1",      name: "1.0 HP Inverter AC",        watts: 900,  defaultQty: 0, defaultHours: 5,  icon: "💨" },
  { id: "ac15",     name: "1.5 HP Inverter AC",        watts: 1350, defaultQty: 0, defaultHours: 4,  icon: "❄️" },
  { id: "pump",     name: "Water Pumping Machine",     watts: 750,  defaultQty: 0, defaultHours: 1,  icon: "🚰" },
  { id: "blender",  name: "Blender & Kitchen Gadgets", watts: 400,  defaultQty: 0, defaultHours: 1,  icon: "🍲" }
];

export default function SolarCalculatorPage() {
  const [quantities, setQuantities] = useState(
    APPLIANCES_PRESET.reduce((acc, app) => ({ ...acc, [app.id]: app.defaultQty }), {})
  );
  const [hours, setHours] = useState(
    APPLIANCES_PRESET.reduce((acc, app) => ({ ...acc, [app.id]: app.defaultHours }), {})
  );
  const [customWatts, setCustomWatts] = useState(0);
  const [customHours, setCustomHours] = useState(6);
  const [backupHours, setBackupHours] = useState(8);

  // Lead Form
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    state: "Lagos",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Calculations
  const { totalWatts, totalDailyWh, recommendedInverter, recommendedBatteryKwh, recommendedPanelsWatts } = useMemo(() => {
    let runningWatts = Number(customWatts) || 0;
    let dailyWh = (Number(customWatts) || 0) * (Number(customHours) || 0);

    APPLIANCES_PRESET.forEach(app => {
      const q = Number(quantities[app.id]) || 0;
      const h = Number(hours[app.id]) || 0;
      runningWatts += app.watts * q;
      dailyWh += app.watts * q * h;
    });

    // Inverter sizing: running load with a 25% safety surge factor
    const inverterLoadWatts = runningWatts * 1.25;
    let inv = "1.0 kVA (12V Pure Sine Wave)";
    if (inverterLoadWatts > 6000) {
      inv = "10.0 kVA (48V Hybrid Solar Inverter)";
    } else if (inverterLoadWatts > 4200) {
      inv = "7.5 kVA (48V Hybrid Solar Inverter)";
    } else if (inverterLoadWatts > 2800) {
      inv = "5.0 kVA (48V Hybrid Solar Inverter)";
    } else if (inverterLoadWatts > 1800) {
      inv = "3.5 kVA (24V/48V Pure Sine Wave)";
    } else if (inverterLoadWatts > 1100) {
      inv = "2.5 kVA (24V Pure Sine Wave)";
    } else if (inverterLoadWatts > 700) {
      inv = "1.5 kVA (12V/24V Pure Sine Wave)";
    }

    // Battery sizing: (runningWatts * backupHours) / (DoD 0.85 * Inverter Eff 0.9)
    const requiredEnergyWh = runningWatts * backupHours;
    const grossWh = requiredEnergyWh / (0.85 * 0.9);
    const battKwh = (grossWh / 1000).toFixed(1);

    // Solar panels: Total daily energy / 4.5 peak sun hours in Nigeria
    const panelsW = Math.ceil(dailyWh / 4.5 / 100) * 100;

    return {
      totalWatts: runningWatts,
      totalDailyWh: Math.round(dailyWh),
      recommendedInverter: inv,
      recommendedBatteryKwh: battKwh,
      recommendedPanelsWatts: Math.max(panelsW, 600)
    };
  }, [quantities, hours, customWatts, customHours, backupHours]);

  function handleQtyChange(id, delta) {
    setQuantities(prev => ({
      ...prev,
      [id]: Math.max(0, (prev[id] || 0) + delta)
    }));
  }

  function handleHoursChange(id, val) {
    setHours(prev => ({
      ...prev,
      [id]: Math.min(24, Math.max(1, Number(val) || 1))
    }));
  }

  function resetCalculator() {
    setQuantities(APPLIANCES_PRESET.reduce((acc, app) => ({ ...acc, [app.id]: app.defaultQty }), {}));
    setHours(APPLIANCES_PRESET.reduce((acc, app) => ({ ...acc, [app.id]: app.defaultHours }), {}));
    setCustomWatts(0);
    setBackupHours(8);
    toast.success("Calculator reset to defaults");
  }

  async function handleLeadSubmit(e) {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Please enter your name and phone number.");
      return;
    }

    setSubmitting(true);
    const leadRecord = {
      name: formData.name,
      email: formData.email || "N/A",
      phone: formData.phone,
      state: formData.state,
      load: `${totalWatts} Watts`,
      hours: `${backupHours} Hours Backup`,
      inverter: recommendedInverter,
      battery: `${recommendedBatteryKwh} kWh Battery Storage`,
      solarArray: `${recommendedPanelsWatts}W Solar Panels`,
      notes: formData.notes || "",
      date: new Date().toLocaleDateString("en-NG"),
      createdAt: new Date().toISOString()
    };

    try {
      // 1. Save to Firestore
      await addDoc(collection(db, "solar_leads"), {
        ...leadRecord,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.warn("Firestore save failed, fallback to local storage:", err);
    }

    // 2. Fallback in localStorage for admin panel compatibility
    try {
      const existing = JSON.parse(localStorage.getItem("afriport_solar_leads") || "[]");
      existing.unshift(leadRecord);
      localStorage.setItem("afriport_solar_leads", JSON.stringify(existing));
    } catch (e) {
      console.error(e);
    }

    setSubmitting(false);
    setSubmitted(true);
    toast.success("Solar consultation requested! An engineer will call you shortly.");
  }

  return (
    <div style={{ background: "#F8FAFC", minHeight: "100vh", paddingBottom: 80 }}>
      {/* ── Header Banner ──────────────────────────────────────────────────────── */}
      <section style={{
        background: "linear-gradient(135deg, #0A1118 0%, #0D1B2A 60%, #162B3D 100%)",
        color: "#fff",
        padding: "60px 20px 70px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{
          position: "absolute",
          top: -100, right: "15%",
          width: 400, height: 400,
          background: "radial-gradient(circle, rgba(22, 163, 74, 0.2) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none"
        }} />

        <div className="container" style={{ position: "relative", zIndex: 1, maxWidth: 840 }}>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(22, 163, 74, 0.18)",
            border: "1px solid rgba(22, 163, 74, 0.4)",
            color: "#4ADE80",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            padding: "6px 16px",
            borderRadius: 9999,
            marginBottom: 20
          }}>
            <Sun size={14} /> Smart Household Solar Sizing Tool
          </div>

          <h1 style={{
            fontFamily: "var(--font-head)",
            fontSize: "clamp(30px, 4vw, 48px)",
            fontWeight: 900,
            color: "#fff",
            marginBottom: 16,
            lineHeight: 1.15
          }}>
            Afriport Solar Load & Sizing Calculator
          </h1>

          <p style={{ fontSize: 17, color: "#CBD5E1", maxWidth: 640, margin: "0 auto 28px", lineHeight: 1.6 }}>
            Accurately estimate your household energy demand, discover your recommended inverter and battery storage capacity, and get tailored factory pricing.
          </p>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/shop?category=Power%20%26%20Solar" className="btn btn-primary" style={{ gap: 8 }}>
              Browse Solar Products <ChevronRight size={16} />
            </Link>
            <button onClick={resetCalculator} className="btn btn-secondary" style={{ gap: 8 }}>
              <RefreshCw size={15} /> Reset Quantities
            </button>
          </div>
        </div>
      </section>

      {/* ── Main Workspace: Calculator + Results ───────────────────────────────── */}
      <div className="container" style={{ marginTop: -32, position: "relative", zIndex: 10 }}>
        <div className="solar-calc-grid">

          {/* Left Column: Appliance Load Builder */}
          <div style={{
            background: "#fff",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--border)",
            boxShadow: "0 8px 30px rgba(13,27,42,0.06)",
            padding: "clamp(16px, 3.5vw, 32px)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: "var(--text)" }}>1. Select Your Appliances</h3>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>Adjust quantities to match your Nigerian home or office</p>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--brand)", background: "var(--brand-light)", padding: "4px 10px", borderRadius: 9999 }}>
                {totalWatts.toLocaleString()} Total Watts
              </span>
            </div>

            {/* Appliance List */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {APPLIANCES_PRESET.map(app => {
                const qty = quantities[app.id] || 0;
                const hrs = hours[app.id] || 0;
                const isSelected = qty > 0;

                return (
                  <div key={app.id} className="appliance-item-row" style={{
                    border: `1.5px solid ${isSelected ? "rgba(232,71,26,0.25)" : "var(--border)"}`,
                    background: isSelected ? "rgba(254, 240, 236, 0.4)" : "#FAFBFC",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                      <span style={{ fontSize: 24 }}>{app.icon}</span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{app.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {app.watts}W each • <strong style={{ color: "var(--text)" }}>{(app.watts * qty).toLocaleString()}W total</strong>
                        </div>
                      </div>
                    </div>

                    {/* Quantity controls */}
                    <div className="appliance-controls-group">
                      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "#fff" }}>
                        <button
                          type="button"
                          onClick={() => handleQtyChange(app.id, -1)}
                          style={{ width: 32, height: 32, border: "none", background: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", color: "var(--text)" }}
                        >
                          −
                        </button>
                        <span style={{ width: 28, textAlign: "center", fontSize: 14, fontWeight: 700 }}>
                          {qty}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQtyChange(app.id, 1)}
                          style={{ width: 32, height: 32, border: "none", background: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", color: "var(--brand)" }}
                        >
                          +
                        </button>
                      </div>

                      {/* Daily hours */}
                      {isSelected && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 6 }}>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={hrs}
                            onChange={e => handleHoursChange(app.id, e.target.value)}
                            style={{
                              width: 44,
                              height: 32,
                              textAlign: "center",
                              borderRadius: "var(--radius-xs)",
                              border: "1px solid var(--border)",
                              fontSize: 12,
                              fontWeight: 600
                            }}
                            title="Daily Operating Hours"
                          />
                          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>hrs</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Custom Extra Load */}
            <div style={{ marginTop: 24, padding: 18, background: "#F1F5F9", borderRadius: "var(--radius-md)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>⚙️ Custom Equipment Wattage</span>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Special machinery, medical gear, etc.</span>
              </div>
              <div className="responsive-two-col" style={{ gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>Extra Watts:</label>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    placeholder="e.g. 500"
                    value={customWatts || ""}
                    onChange={e => setCustomWatts(Math.max(0, Number(e.target.value)))}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--border)",
                      marginTop: 4,
                      background: "#fff"
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>Daily Run Hours:</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={customHours}
                    onChange={e => setCustomHours(Math.max(1, Number(e.target.value)))}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--border)",
                      marginTop: 4,
                      background: "#fff"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Target Backup Hours Selector */}
            <div style={{ marginTop: 28 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>
                2. Desired Daily Battery Backup Duration
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 75px), 1fr))", gap: 10 }}>
                {[4, 8, 12, 24].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setBackupHours(h)}
                    style={{
                      padding: "12px 10px",
                      borderRadius: "var(--radius-sm)",
                      border: backupHours === h ? "2px solid var(--brand)" : "1px solid var(--border)",
                      background: backupHours === h ? "var(--brand-light)" : "#fff",
                      color: backupHours === h ? "var(--brand)" : "var(--text)",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    {h} Hours {h === 8 ? "⭐" : ""}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Recommendations & Consultation Lead Form */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Recommendation Summary Card */}
            <div style={{
              background: "#0D1B2A",
              color: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: 28,
              boxShadow: "0 12px 40px rgba(13,27,42,0.15)",
              border: "1px solid rgba(255,255,255,0.1)",
              position: "relative",
              overflow: "hidden"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <Zap size={22} color="#F5A623" />
                <h3 style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>System Recommendation</h3>
              </div>

              {/* Metric 1: Inverter */}
              <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.06)", borderRadius: "var(--radius-md)", marginBottom: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94A3B8", fontWeight: 700 }}>
                  Recommended Inverter
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#FBBF24", marginTop: 4 }}>
                  {recommendedInverter}
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                  Peak Load: <strong>{totalWatts.toLocaleString()}W</strong> • Daily: <strong>{(totalDailyWh / 1000).toFixed(1)} kWh</strong>
                </div>
              </div>

              {/* Metric 2: Battery Capacity */}
              <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.06)", borderRadius: "var(--radius-md)", marginBottom: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94A3B8", fontWeight: 700 }}>
                  Recommended Battery Storage
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#34D399", marginTop: 4 }}>
                  {recommendedBatteryKwh} kWh Capacity
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                  Supports {backupHours} hours continuous runtime (~{Math.round(Number(recommendedBatteryKwh) * 1000 / 48)}Ah @ 48V LiFePO4)
                </div>
              </div>

              {/* Metric 3: Solar Array */}
              <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.06)", borderRadius: "var(--radius-md)", marginBottom: 20 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94A3B8", fontWeight: 700 }}>
                  Recommended Solar PV Array
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#60A5FA", marginTop: 4 }}>
                  {recommendedPanelsWatts.toLocaleString()}W Solar Panels
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                  ~{Math.ceil(recommendedPanelsWatts / 450)}x 450W Monocrystalline Panels
                </div>
              </div>

              <Link
                to="/shop?category=Power%20%26%20Solar"
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", fontWeight: 700, gap: 8 }}
              >
                Browse Solar Products Now <ArrowRight size={16} />
              </Link>
            </div>

            {/* Lead Consultation Request Card */}
            <div style={{
              background: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: 28,
              border: "1px solid var(--border)",
              boxShadow: "0 8px 30px rgba(13,27,42,0.06)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <PhoneCall size={20} color="var(--brand)" />
                <h4 style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>Request Free Engineering Quote</h4>
              </div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
                Our certified solar technicians will review your load sizing and call you with an exact turn-key quote.
              </p>

              {submitted ? (
                <div style={{
                  padding: 24,
                  background: "#DCFCE7",
                  border: "1px solid #86EFAC",
                  borderRadius: "var(--radius-md)",
                  textAlign: "center"
                }}>
                  <CheckCircle size={36} color="#16A34A" style={{ margin: "0 auto 12px" }} />
                  <h4 style={{ fontSize: 16, fontWeight: 800, color: "#166534", marginBottom: 6 }}>Request Sent Successfully!</h4>
                  <p style={{ fontSize: 13, color: "#15803D", lineHeight: 1.5 }}>
                    An Afriport solar specialist will contact you on <strong>{formData.phone}</strong> with a detailed installation quote.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="btn btn-outline btn-sm"
                    style={{ marginTop: 14 }}
                  >
                    Submit Another Consultation
                  </button>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Babatunde Adeleke"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--border)",
                        marginTop: 4,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Phone Number (WhatsApp) *</label>
                    <input
                      type="tel"
                      required
                      placeholder="e.g. 0803 123 4567"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--border)",
                        marginTop: 4,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Email Address (Optional)</label>
                    <input
                      type="email"
                      placeholder="e.g. name@gmail.com"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--border)",
                        marginTop: 4,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Installation State *</label>
                    <select
                      value={formData.state}
                      onChange={e => setFormData({ ...formData, state: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--border)",
                        marginTop: 4,
                        fontSize: 14,
                        background: "#fff"
                      }}
                    >
                      {["Lagos", "Abuja FCT", "Rivers (PH)", "Oyo (Ibadan)", "Kano", "Edo", "Delta", "Ogun", "Enugu", "Kaduna", "Other 36 States"].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary"
                    style={{ width: "100%", justifyContent: "center", marginTop: 6, fontWeight: 700 }}
                  >
                    {submitting ? "Sending..." : "Submit Consultation Request"}
                  </button>
                </form>
              )}
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
